import { techMarkerHtml } from "./TechMarker"
import Job from "../../modules/crm/jobs/models/Job"
import Technician from "../../modules/technicians/models/technician"
import TechAvailabilityLabel from "../tech/TechAvailabilityLabel"
import TechLocationFreshnessLabel from "../tech/TechLocationFreshness"
import TechLocationAccessLabel from "../tech/TechLocationAccessLabel"
import { TechBatteryBadge } from "../tech/TechBatteryLabel"
import TechAppStateLabel from "../tech/TechAppStateLabel"

interface IProps {
    tech: Technician
    /** Every job on the board that is theirs — only counted here, never listed. */
    jobs: Job[]
    /** True when the pin is on their wake-up address because no position was ever reported. */
    isWakeupAddress?: boolean
}

/**
 * What hovering a technician on the map should tell you.
 *
 * Short on purpose. Every technician's card is the same height, so a map with twenty of them
 * stays a map instead of a wall of panels — and the one thing that would make it grow, their
 * list of jobs, is a number here and a list in the drawer a click away.
 *
 * Rendered to a string and handed to Leaflet, so it carries no handlers; the map opens the
 * drawer when the pin itself is clicked.
 */
export default function TechMapPopup(props: IProps) {

    const inProgress = props.jobs.filter(job => job.isInProgress).length

    return (
        <div style={{ width: 250 }} className="tech-map-popup">

            <div className="d-flex align-items-center">
                {/* The same face, and the same colour, as the pin this was opened from — so
                    the panel is plainly about that one rather than about a name. */}
                <div
                    className="me-3 flex-shrink-0"
                    style={{ width: 44, height: 44 }}
                    dangerouslySetInnerHTML={{ __html: techMarkerHtml(props.tech, { size: 44, plain: true }) }}
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-bold fs-6 text-start text-truncate">
                        {props.tech.fullName}
                    </div>
                    {/* Always a second line, phone or handle, so every card is the same
                        height and a map of them lines up. */}
                    <div className="text-muted fs-8 text-start mt-1 text-truncate">
                        {props.tech.phoneItem?.phoneFriendly ?? `@${props.tech.username ?? ""}`}
                    </div>
                </div>
                {/* Their phone's battery, opposite the name: the first thing to know about
                    somebody who might be about to drop off the map. */}
                <div className="ms-3 flex-shrink-0 align-self-center">
                    <TechBatteryBadge techId={props.tech.id} />
                </div>
            </div>

            {/* First, because it is the first thing that explains a quiet dot. */}
            <div className="mt-3 fs-8 text-start">
                <TechAppStateLabel techId={props.tech.id} />
            </div>

            <div className="fs-8 text-start mt-2">
                <TechLocationFreshnessLabel
                    techId={props.tech.id}
                    lastUpdate={props.tech.techLocationLastUpdate}
                    isWakeupAddress={props.isWakeupAddress}
                />
            </div>

            <div className="fs-8 text-start mt-2">
                <TechLocationAccessLabel techId={props.tech.id} compact />
            </div>


            <div className="fs-8 text-start mt-2">
                <TechAvailabilityLabel
                    availability={props.tech.availability}
                    fallback={<span className="text-gray-500">Working hours not set</span>}
                />
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3 pt-3"
                style={{ borderTop: "1px solid rgba(0,0,0,.08)" }}>
                <div className="fw-bold fs-8 text-start text-gray-700">
                    {props.jobs.length === 0
                        ? "No jobs"
                        : `${props.jobs.length} job${props.jobs.length === 1 ? "" : "s"}`}
                    {inProgress > 0 &&
                        <span className="text-danger ms-2">· {inProgress} in progress</span>
                    }
                </div>
                <div className="fs-9 text-gray-500">Click for details</div>
            </div>
        </div>
    )
}
